import { FieldProps, FormikProps, getIn } from 'formik';
import _ from 'lodash';
import * as React from 'react';
import { OnChangeValue } from 'react-select';
import { AsyncSelect, IAsyncSelectProps } from '../../select/async';
import { ISelectOption } from '../../select/select';
// import { AsyncSelect, IAsyncSelectProps, ISelectOption } from '../../';
import * as S from './async-select.styles';

type IAsyncSelectFieldProps = IAsyncSelectProps &
    FieldProps & {
        loadOptions: (inputValue: string) => Promise<ISelectOption[]>;
        onChange?: (option: OnChangeValue<ISelectOption, boolean>, form: FormikProps<any>) => Promise<void> | void;
        getDefault?: (id: string, form: FormikProps<any>) => Promise<ISelectOption | null>;
        postfixComponent?: React.ReactElement;
    };

/**
 * Wrapper around BaseSelect to be able to work with Form components
 */
export const AsyncSelectField: React.FunctionComponent<IAsyncSelectFieldProps> = (
    props: IAsyncSelectFieldProps,
): React.ReactElement => {
    const { field, form, postfixComponent, defaultOptions = true, cacheOptions = true, ...otherProps } = props;
    const error = getIn(form.errors, field.name);
    const touched = getIn(form.touched, field.name);
    const errorMessage: string = touched && error ? error : null;

    const [selectedValue, setSelectedValue] = React.useState<ISelectOption | null>(
        field.value && typeof field.value === 'object' ? field.value : null,
    );
    const [disabled, setDisabled] = React.useState(false);

    React.useEffect(() => {
        if (selectedValue?.value !== field.value) {
            updateSelectedValue(field.value);
        }
    }, [field.value]);

    const updateSelectedValue = async (value: string): Promise<void> => {
        if (!props.getDefault) {
            return;
        }

        if (!value) {
            setSelectedValue(null);
            return;
        }

        // Do not disable the select while resolving getDefault — toggling isDisabled
        // remounts/resets AsyncSelect option loading (defaultOptions + cacheOptions)
        // and can leave the menu empty even when the API returned data.
        try {
            const defaultValue = await props.getDefault(value, form);
            setSelectedValue(defaultValue ?? { label: String(value), value });
        } catch (e) {
            console.warn('AsyncSelectField getDefault failed', e);
            setSelectedValue({ label: String(value), value });
        }
    };

    // `handleChange` keeps a stable identity on purpose — an unstable onChange races
    // AsyncSelect's cacheOptions. It must NOT close over `props`/`form`/`field` though:
    // consumers read `form.values` to build the next form state, and a bag captured on
    // the first render hands them a mount-time snapshot. Formik's setters survive that
    // (they are useEventCallback-stable) but `values` does not, so a consumer's
    // `setValues` would write stale fields back over everything the user has since
    // edited. Read the latest bag out of a ref instead.
    //
    // The ref is refreshed in a LAYOUT effect, not during render: under a concurrent
    // root a render can be thrown away, and a render-phase write would leave this
    // handler reading a bag that never committed. Layout rather than passive because
    // passive effects can be deferred, and a click must not beat the update to it.
    // This is the same shape as Formik's own `useEventCallback`.
    const latestRef = React.useRef({ props, form, field });

    React.useLayoutEffect(() => {
        latestRef.current = { props, form, field };
    });

    const handleChange = React.useCallback(async (option: OnChangeValue<ISelectOption, boolean>) => {
        const { props: latestProps, form: latestForm, field: latestField } = latestRef.current;

        // Handle clear (when option is null/undefined)
        if (!option) {
            setSelectedValue(null);
            setDisabled(true);
            try {
                await latestProps.onChange?.(null, latestForm);
            } finally {
                setDisabled(false);
            }
            latestForm.setFieldValue(latestField.name, null);
            latestForm.setFieldTouched(latestField.name, true, false);
            return;
        }

        const selectedOption = option as ISelectOption;

        setSelectedValue(selectedOption);
        setDisabled(true);
        try {
            await latestProps.onChange?.(selectedOption, latestForm);
        } finally {
            setDisabled(false);
        }

        // In case of multiselect
        if (Array.isArray(option)) {
            latestForm.setFieldValue(
                latestField.name,
                option.map(a => a.value),
            );
        } else {
            latestForm.setFieldValue(latestField.name, _.get(option, 'value', null));
        }

        // Mark as touched since we use validateOnBlur=false
        latestForm.setFieldTouched(latestField.name, true, false);
    }, []);

    const postfix = _.isFunction(postfixComponent) ? postfixComponent(selectedValue) : postfixComponent;

    return (
        <S.SelectContainer>
            <AsyncSelect
                {...otherProps}
                {...field}
                // AsyncSelect maps the `disabled` prop to react-select's isDisabled
                disabled={disabled || props.disabled || props.isDisabled}
                errorMessage={errorMessage}
                menuPosition="fixed"
                value={selectedValue}
                onChange={handleChange}
                defaultOptions={defaultOptions}
                cacheOptions={cacheOptions}
            />
            <S.PostfixContainer>{postfix}</S.PostfixContainer>
        </S.SelectContainer>
    );
};
